import { Card, Col, Container, Row, Image, Table, Badge } from "react-bootstrap";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { goBack } from "../../../utils/otherHelopers";
import { routes } from "../../../routes/pageRoutes";
import { fetchAdminBookingDetails } from "../../../redux/slice/marketplaceSlice";
import { useLoader } from "../../../context/LoaderContext";
import { ArrowLeftIcon } from "../../../assets/icons";
import StatusChip from "../../../components/statusChip/StatusChip";
import { formatMomentDate } from "../../../utils/dateHelper";
import PreviewModal from "../../../components/modals/previewModal";
import { createPortal } from "react-dom";

const cleanDeletedString = (str) => {
    if (!str) return "-";
    if (str.startsWith("DELETED_")) {
        const parts = str.split("_");
        if (parts.length >= 3) {
            return parts.slice(2).join("_");
        }
    }
    return str;
};

const renderHeaderContent = (onBack) => (
    <div className="d-flex align-items-center gap-3 w-100">
        <div className="d-flex align-items-center gap-2">
            <button
                onClick={onBack}
                type="button"
                className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center primary"
            >
                <ArrowLeftIcon size={22} strokeWidth="2.2" />
            </button>
            <h4 className="fw-semibold mb-0" style={{ fontSize: '18px' }}>
                Marketplace Booking Details
            </h4>
        </div>
    </div>
);

const ViewBooking = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { setLoading } = useLoader();

    const { adminBookingDetails, isLoading } = useSelector((state) => state.marketplace);
    const [previewMedia, setPreviewMedia] = useState(null); // { type: 'video' | 'document', url: string, title: string }

    useEffect(() => {
        if (id) {
            setLoading(true);
            dispatch(fetchAdminBookingDetails(id)).finally(() => setLoading(false));
        }
    }, [id, dispatch, setLoading]);

    if (isLoading) return null;

    if (!adminBookingDetails) {
        return <div className="p-5 text-center">Booking not found</div>;
    }

    const booking = adminBookingDetails;
    const buyer = booking?.user;
    const listing = booking?.listing;
    const payment = booking?.payment;

    const productDetails = listing?.company_product_details || listing?.user_listing_details;
    const offerDetails = listing?.company_offer_details;
    const sellerInfo = listing?.company_seller_info;

    const isCompanySeller = !!listing?.company;
    const seller = isCompanySeller ? listing?.company : listing?.user;
    const sellerName = isCompanySeller
        ? seller?.name
        : seller ? `${seller.first_name || ""} ${seller.last_name || ""}`.trim() : "Unknown Seller";

    const isBuyerDeleted = buyer?.deleted_at || (buyer?.mobile_number && buyer.mobile_number.startsWith("DELETED_"));
    const isSellerDeleted = !isCompanySeller && (seller?.deleted_at || (seller?.mobile_number && seller.mobile_number.startsWith("DELETED_")));
    const isListingDeleted = !!listing?.deleted_at;

    const canRedirectSeller = isCompanySeller ? !!seller?.id : (!isSellerDeleted && seller?.id);
    const handleSellerClick = () => {
        if (!canRedirectSeller) return;
        if (isCompanySeller) {
            navigate(routes.viewCompany.replace(":id", seller.id), { state: { from: location.pathname } });
        } else {
            navigate(`/user/user-detail/${seller.id}`, { state: { from: location.pathname } });
        }
    };

    const headerPortalTarget = document.getElementById("header-portal-target");
    const handleBack = () => {
        goBack(navigate, location, routes.marketplaceBookings);
    };

    return (
        <>
            {headerPortalTarget && createPortal(renderHeaderContent(handleBack), headerPortalTarget)}
            <Container fluid className="px-0">
                <Row className=" gy-4">
                    {/* Left Column: Booking & Listing Info */}
                    <Col xl={8}>
                        <Card className="rounded-2 p-4 border-0 common-shadow mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="fw-medium mb-0">Booking Information</h5>
                                <StatusChip
                                    pill
                                    bg={
                                        booking?.booking_status === "pending"
                                            ? "warning"
                                            : booking?.booking_status === "confirmed"
                                                ? "success"
                                                : "danger"
                                    }
                                    title={String(booking?.booking_status || "")}
                                    className="text-capitalize"
                                />
                            </div>

                            <div className="info-list">
                                <Row>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <p className="text-mutedSmall mb-1">Booking ID</p>
                                            <p className="fw-medium">{booking?.id}</p>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <p className="text-mutedSmall mb-1">Booking Date</p>
                                            <p className="fw-medium">{booking?.created_at ? formatMomentDate(booking.created_at, "DD MMM YYYY, hh:mm A") : "-"}</p>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <p className="text-mutedSmall mb-1">Delivery Address</p>
                                            <p className="fw-medium">{String(booking?.delivery_address || "-")}</p>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <p className="text-mutedSmall mb-1">Delivery City</p>
                                            <p className="fw-medium">{String(booking?.city?.name || "-")}</p>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </Card>

                        <Card className="rounded-2 p-4 border-0 common-shadow mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="fw-medium mb-0">Listing Details</h5>
                                {!isListingDeleted && (
                                    <button
                                        className="btn btn-link p-0 text-decoration-none fw-bold small"
                                        style={{ color: '#8b0000', fontSize: '0.85rem' }}
                                        onClick={() => navigate(`/marketplace/bulk-offers/${listing?.id}`)}
                                    >
                                        View Full Listing →
                                    </button>
                                )}
                            </div>
                            {/* <ProfileCard title={'Buyer Information'} user={{
                        name: `${buyer?.first_name} ${buyer?.last_name}`,
                        image: buyer?.profile?.profile_photo?.url,
                        email: buyer?.email,
                        role: buyer?.profile?.profession?.name,
                        mobile_no: buyer?.mobile_number,
                    }} />
                   <ProfileCard title={'Seller Information'} className="mt-4" user={{
                        name: sellerName,
                        image: isCompanySeller ? seller?.profile?.logo?.url : seller?.profile?.profile_photo?.url,
                        email: isCompanySeller ? seller?.profile?.email : seller?.email,
                        role: isCompanySeller ? "Company" : "Individual",
                        mobile_no: isCompanySeller ? seller?.profile?.mobile_number : seller?.mobile_number,
                    }} /> */}
                            {/* Media Gallery */}
                            <div className="mb-4">
                                {/* Standard Photos */}
                                {(listing?.photos?.length > 0 || (!listing?.photos && listing?.media?.length > 0)) ? (
                                    <div className="mb-3">
                                        <p className="text-mutedSmall mb-2">Product Photos</p>
                                        <div className="d-flex gap-2 overflow-auto pb-2 no-scrollbar">
                                            {(listing?.photos || listing?.media || []).map((item, idx) => (
                                                <Image
                                                    key={idx}
                                                    src={item.url}
                                                    alt={`Photo ${idx}`}
                                                    className="rounded-2 object-fit-cover border"
                                                    style={{ minWidth: '120px', width: '120px', height: '120px', cursor: 'pointer' }}
                                                    onError={(e) => { e.target.src = 'https://placehold.co/120x120?text=No+Image'; }}
                                                    onClick={() => setPreviewMedia({ type: 'image', url: item.url, title: `Product Photo ${idx + 1}` })}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ) : null}

                                {/* A+ Content */}
                                {listing?.a_plus_photos?.length > 0 && (
                                    <div className="mb-3">
                                        <p className="text-mutedSmall mb-2">A+ Marketing Content</p>
                                        <div className="d-flex gap-2 overflow-auto pb-2 no-scrollbar">
                                            {listing.a_plus_photos.map((item, idx) => (
                                                <Image
                                                    key={idx}
                                                    src={item.url}
                                                    alt={`A+ Photo ${idx}`}
                                                    className="rounded-2 object-fit-cover border"
                                                    style={{ minWidth: '200px', width: '200px', height: '120px', cursor: 'pointer' }}
                                                    onError={(e) => { e.target.src = 'https://placehold.co/200x120?text=A%2B+Content'; }}
                                                    onClick={() => setPreviewMedia({ type: 'image', url: item.url, title: `A+ Marketing Content ${idx + 1}` })}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Video & Documents */}
                                {(listing?.video || listing?.document) && (
                                    <div className="d-flex gap-3">
                                        {listing?.video && (
                                            <div className="flex-grow-1">
                                                <p className="text-mutedSmall mb-2">Product Video</p>
                                                <div
                                                    className="rounded-2 border d-flex align-items-center justify-content-center position-relative overflow-hidden bg-dark"
                                                    style={{ height: '100px', cursor: 'pointer' }}
                                                    onClick={() => setPreviewMedia({ type: 'video', url: listing.video.url, title: 'Product Video' })}
                                                >
                                                    {listing.video.thumbnail_url ? (
                                                        <img
                                                            src={listing.video.thumbnail_url}
                                                            alt="Video Preview"
                                                            className="position-absolute w-100 h-100 object-fit-cover opacity-50"
                                                        />
                                                    ) : (
                                                        <div className="position-absolute w-100 h-100 bg-secondary opacity-25"></div>
                                                    )}
                                                    <div className="text-center position-relative z-index-1">
                                                        <span className="d-block small fw-bold text-white">▶ Play Video</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {listing?.document && (
                                            <div className="flex-grow-1">
                                                <p className="text-mutedSmall mb-2">Documentation</p>
                                                <div
                                                    className="rounded-2 border d-flex align-items-center justify-content-center bg-light"
                                                    style={{ height: '100px', cursor: 'pointer' }}
                                                    onClick={() => setPreviewMedia({ type: 'document', url: listing.document.url, title: 'Documentation' })}
                                                >
                                                    <div className="text-center">
                                                        <span className="d-block small fw-bold text-success">📄 View Doc</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!listing?.photos?.length && !listing?.a_plus_photos?.length && !listing?.video && !listing?.document && !listing?.media?.length && (
                                    <Image src="https://placehold.co/120x120?text=No+Media" className="rounded-2 border" />
                                )}
                            </div>

                            {/* Media Preview Modal */}
                            <PreviewModal
                                show={!!previewMedia}
                                onHide={() => setPreviewMedia(null)}
                                previewMedia={previewMedia}
                            />

                            <div className="mb-4">
                                <div className="d-flex align-items-center gap-1 mb-1">
                                    <h6 className={`fw-bold mb-0 ${isListingDeleted ? "text-secondary fst-italic" : ""}`}>
                                        {String(listing?.name || "")}
                                    </h6>
                                    {isListingDeleted && (
                                        <Badge bg="danger" className="text-capitalize px-2 py-1 rounded-1" style={{ fontSize: "10px", lineHeight: "1" }}>
                                            Deleted
                                        </Badge>
                                    )}
                                </div>
                                <p className="text-mutedSmall mb-3">{String(listing?.category?.name || "")} • {String(listing?.city?.name || "")}</p>

                                <Row className="gy-4">
                                    <Col md={4}>
                                        <p className="text-mutedSmall mb-1">Pricing</p>
                                        <div className="d-flex align-items-center gap-2">
                                            <h5 className="text-primary fw-bold mb-0">₹ {parseFloat(offerDetails?.offer_price || listing?.price || 0).toLocaleString()}</h5>
                                            {offerDetails?.mrp > offerDetails?.offer_price && (
                                                <span className="text-mutedSmall text-decoration-line-through small">₹ {parseFloat(offerDetails.mrp).toLocaleString()}</span>
                                            )}
                                        </div>
                                        {(offerDetails?.discount > 0 || (offerDetails?.mrp > offerDetails?.offer_price)) && (
                                            <span className="badge bg-success-subtle text-success small rounded-1 mt-1">
                                                {offerDetails?.discount || Math.round(((offerDetails.mrp - offerDetails.offer_price) / offerDetails.mrp) * 100)}% OFF
                                            </span>
                                        )}
                                    </Col>
                                    <Col md={4}>
                                        <p className="text-mutedSmall mb-1">SKU / Model</p>
                                        <p className="fw-medium">{String(productDetails?.sku || "-")}</p>
                                    </Col>
                                    <Col md={4}>
                                        <p className="text-mutedSmall mb-1">Warranty</p>
                                        <p className="fw-medium">{String(productDetails?.warranty || "No Warranty")}</p>
                                    </Col>

                                    <Col md={4}>
                                        <p className="text-mutedSmall mb-1">Available Quantity</p>
                                        <p className="fw-medium">{offerDetails?.available_qty ? `${offerDetails.available_qty} Units` : "-"}</p>
                                    </Col>
                                    <Col md={4}>
                                        <p className="text-mutedSmall mb-1">Lot Size</p>
                                        <p className="fw-medium">{offerDetails?.lot_size ? `${offerDetails.lot_size} Units` : "-"}</p>
                                    </Col>
                                    <Col md={4}>
                                        <p className="text-mutedSmall mb-1">Quantity (Variation)</p>
                                        <p className="fw-medium">{String(productDetails?.variations || "-")}</p>
                                    </Col>

                                    <Col md={4}>
                                        <p className="text-mutedSmall mb-1">Country of Origin</p>
                                        <p className="fw-medium">{String(productDetails?.country?.name || "India")}</p>
                                    </Col>
                                    <Col md={4}>
                                        <p className="text-mutedSmall mb-1">Serviceability</p>
                                        <p className="fw-medium text-capitalize">{String(offerDetails?.serviceability_type || "-").replace("_", " ")}</p>
                                    </Col>
                                </Row>
                            </div>

                            {isCompanySeller && sellerInfo && (
                                <div className="border-top mt-4 pt-4">
                                    <h6 className="fw-bold small text-uppercase mb-3">Seller Manufacturing Info</h6>
                                    <Row className="gy-3">
                                        <Col md={6}>
                                            <p className="text-mutedSmall mb-1">Manufactured By</p>
                                            <p className="fw-medium small">{String(sellerInfo.manufactured_by || "-")}</p>
                                        </Col>
                                        <Col md={6}>
                                            <p className="text-mutedSmall mb-1">Marketed By</p>
                                            <p className="fw-medium small">{String(sellerInfo.marketed_by || "-")}</p>
                                        </Col>
                                        <Col md={12}>
                                            <p className="text-mutedSmall mb-1">Plant Address</p>
                                            <p className="fw-medium small">{String(sellerInfo.plant_address || "-")}</p>
                                        </Col>
                                    </Row>
                                </div>
                            )}
                        </Card>

                        <Card className="rounded-2 p-4 border-0 common-shadow">
                            <h5 className="fw-medium mb-4">Order Summary</h5>
                            <Table responsive borderless className="mb-0">
                                <thead>
                                    <tr className="border-bottom">
                                        <th className="ps-0 text-mutedSmall">ITEM</th>
                                        <th className="text-center text-mutedSmall">LOTS</th>
                                        <th className="text-center text-mutedSmall">QTY / LOT</th>
                                        <th className="text-center text-mutedSmall">TOTAL QTY</th>
                                        <th className="text-end pe-0 text-mutedSmall">DEPOSIT AMOUNT</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-bottom">
                                        <td className="ps-0 py-3 fw-medium">{String(listing?.name || "")}</td>
                                        <td className="text-center py-3">{String(booking?.lots_booked || 0)}</td>
                                        <td className="text-center py-3">{String(offerDetails?.lot_size || 0)} Units</td>
                                        <td className="text-center py-3">{((booking?.lots_booked || 0) * (offerDetails?.lot_size || 0)).toLocaleString()} Units</td>
                                        <td className="text-end pe-0 py-3 fw-medium">₹ {parseFloat(booking?.total_amount || 0).toLocaleString()}</td>
                                    </tr>
                                    <tr className="border-top">
                                        <td colSpan="4" className="text-end pt-4 fw-bold">Net Payable Amount</td>
                                        <td className="text-end pt-4 fw-bold text-primary fs-5">₹ {parseFloat(payment?.amount || booking?.total_amount || 0).toLocaleString()}</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Card>
                    </Col>

                    {/* Right Column: Buyer & Seller Info */}
                    <Col xl={4}>
                        <Card className="rounded-2 p-4 border-0 common-shadow mb-4">
                            <h5 className="fw-medium mb-4">Buyer Information</h5>
                            <div
                                className={`d-flex align-items-center gap-3 mb-4 ${!isBuyerDeleted ? "cursor-pointer" : ""}`}
                                onClick={() => {
                                    if (!isBuyerDeleted && buyer?.id) {
                                        navigate(`/user/user-detail/${buyer.id}`, { state: { from: location.pathname } });
                                    }
                                }}
                            >
                                <div style={isBuyerDeleted ? { opacity: 0.5 } : {}}>
                                    <Image
                                        src={buyer?.profile?.profile_photo?.url || "https://placehold.co/50x50?text=B"}
                                        roundedCircle
                                        width={50}
                                        height={50}
                                        className="object-fit-cover"
                                    />
                                </div>
                                <div>
                                    <div className="d-flex align-items-center gap-1">
                                        <h6 className={`mb-0 fw-bold ${isBuyerDeleted ? "text-secondary fst-italic" : ""}`}>
                                            {String(buyer?.first_name || "")} {String(buyer?.last_name || "")}
                                        </h6>
                                        {isBuyerDeleted && (
                                            <Badge bg="danger" className="text-capitalize px-2 py-1 rounded-1 ms-1" style={{ fontSize: "10px", lineHeight: "1" }}>
                                                Deleted
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="text-mutedSmall mb-0">
                                        {isBuyerDeleted ? "Deleted User" : String(buyer?.profile?.profession?.name || "User")}
                                    </p>
                                </div>
                            </div>
                            <div className="info-list">
                                <div className="d-flex justify-content-between border-bottom py-2">
                                    <span className="text-mutedSmall">Mobile</span>
                                    <span className="fw-medium">{cleanDeletedString(buyer?.mobile_number)}</span>
                                </div>
                                <div className="d-flex justify-content-between border-bottom py-2">
                                    <span className="text-mutedSmall">Email</span>
                                    <span className="fw-medium">{cleanDeletedString(buyer?.email)}</span>
                                </div>
                            </div>
                        </Card>

                        <Card className="rounded-2 p-4 border-0 common-shadow mb-4">
                            <h5 className="fw-medium mb-4">Seller Information</h5>
                            <div
                                className={`d-flex align-items-center gap-3 mb-4 ${canRedirectSeller ? "cursor-pointer" : ""}`}
                                onClick={handleSellerClick}
                            >
                                <div style={isSellerDeleted ? { opacity: 0.5 } : {}}>
                                    <Image
                                        src={isCompanySeller ? seller?.profile?.logo?.url : seller?.profile?.profile_photo?.url || "https://placehold.co/50x50?text=S"}
                                        roundedCircle={!isCompanySeller}
                                        className="object-fit-cover rounded-2"
                                        width={50}
                                        height={50}
                                    />
                                </div>
                                <div>
                                    <div className="d-flex align-items-center gap-1">
                                        <h6 className={`mb-0 fw-bold ${isSellerDeleted ? "text-secondary fst-italic" : ""}`}>
                                            {String(sellerName || "")}
                                        </h6>
                                        {isSellerDeleted && (
                                            <Badge bg="danger" className="text-capitalize px-2 py-1 rounded-1 ms-1" style={{ fontSize: "10px", lineHeight: "1" }}>
                                                Deleted
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="text-mutedSmall mb-0 text-capitalize">
                                        {isSellerDeleted ? "Deleted User" : (isCompanySeller ? "Company" : "Individual")}
                                    </p>
                                </div>
                            </div>
                            <div className="info-list">
                                <div className="d-flex justify-content-between border-bottom py-2">
                                    <span className="text-mutedSmall">Mobile</span>
                                    <span className="fw-medium">
                                        {isCompanySeller ? (seller?.profile?.mobile_number || "-") : cleanDeletedString(seller?.mobile_number)}
                                    </span>
                                </div>
                                <div className="d-flex justify-content-between border-bottom py-2">
                                    <span className="text-mutedSmall">Email</span>
                                    <span className="fw-medium">
                                        {isCompanySeller ? (seller?.profile?.email || "-") : cleanDeletedString(seller?.email)}
                                    </span>
                                </div>
                            </div>
                        </Card>

                        {/* {payment && (
                        <Card className="rounded-2 p-4 border-0 common-shadow">
                            <h5 className="fw-medium mb-4">Payment Details</h5>
                            <div className="info-list">
                                <div className="d-flex justify-content-between border-bottom py-2">
                                    <span className="text-mutedSmall">Payment Status</span>
                                    <StatusChip
                                        bg={payment.payment_status === 'paid' ? 'success' : 'danger'}
                                        title={payment.payment_status}
                                        pill
                                    />
                                </div>
                                <div className="d-flex justify-content-between border-bottom py-2">
                                    <span className="text-mutedSmall">Transaction ID</span>
                                    <span className="fw-medium text-truncate" style={{ maxWidth: '150px' }}>{String(payment.cf_payment_id || payment.transaction_id || "-")}</span>
                                </div>
                                <div className="d-flex justify-content-between border-bottom py-2">
                                    <span className="text-mutedSmall">Method</span>
                                    <span className="fw-medium text-capitalize">{typeof (payment.payment_group || payment.payment_method) === 'object' ? Object.keys(payment.payment_group || payment.payment_method)[0] : String(payment.payment_group || payment.payment_method || "-")}</span>
                                </div>
                                <div className="d-flex justify-content-between border-bottom py-2">
                                    <span className="text-mutedSmall">Service Charge</span>
                                    <span className="fw-medium">₹ {parseFloat(payment.service_charge || 0).toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between border-bottom py-2">
                                    <span className="text-mutedSmall">Service Tax</span>
                                    <span className="fw-medium">₹ {parseFloat(payment.service_tax || 0).toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between border-bottom py-2">
                                    <span className="text-mutedSmall">Settlement Amount</span>
                                    <span className="fw-bold text-success">₹ {parseFloat(payment.settlement_amount || 0).toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between py-2">
                                    <span className="text-mutedSmall">Paid On</span>
                                    <span className="fw-medium">{payment.payment_time ? formatMomentDate(payment.payment_time, "DD MMM YYYY, hh:mm A") : "-"}</span>
                                </div>
                            </div>
                        </Card>
                    )} */}
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default ViewBooking;
